"use client"

import { useEffect, useState } from "react"
import { CalendarPlus } from "lucide-react"

import {
  googleCalendarUrl,
  office365Url,
  outlookLiveUrl,
  yahooCalendarUrl,
  type CalendarEvent,
} from "@/lib/booking/calendar-links"
import { marketingActionClass } from "@/components/marketing/marketing-action"

/**
 * Save-the-call, on whichever calendar he actually uses.
 *
 * Two owner corrections built this. The page offered Google alone, and the
 * Google entry it built carried no call details at all, because the page only
 * had a booking uid and filled `details` with the fixed string "your call link
 * is in the confirmation email". So the saved entry knew a call existed and
 * nothing else.
 *
 * The real booking now comes from /api/booking/calendar, so every option gets
 * the meeting link in both the description and the location, which is what turns
 * the calendar reminder into one tap.
 *
 * It degrades rather than disappearing: with no uid or a failed lookup it
 * falls back to the times already in the URL and a neutral "Call details"
 * location instead of guessing at a meeting link. A booked prospect never
 * loses the button because a lookup failed.
 */
export function AddToCalendar({
  uid,
  fallbackStart,
  fallbackEnd,
}: {
  uid: string | null
  fallbackStart: string | null
  fallbackEnd: string | null
}) {
  // The fallback entry is pure derivation from props, so it is computed during
  // render rather than assigned from an effect. See web/AGENTS.md: effects
  // synchronize with external systems; derive validation-only render state
  // directly or isolate it behind a component boundary.
  const fallbackEvent: CalendarEvent | null =
    fallbackStart && fallbackEnd
      ? {
          title: "Cold Approach application call",
          description: "Your application call with a Cold Approach coach.",
          location: "Call details",
          start: fallbackStart,
          end: fallbackEnd,
        }
      : null

  if (!uid) return <CalendarMenu event={fallbackEvent} uid={null} />

  // Keyed on uid so a different booking remounts and drops the previous
  // lookup's result instead of showing it against the new booking.
  return <BookedCalendar key={uid} uid={uid} fallbackEvent={fallbackEvent} />
}

/** Owns only the asynchronous booking lookup. */
function BookedCalendar({
  uid,
  fallbackEvent,
}: {
  uid: string
  fallbackEvent: CalendarEvent | null
}) {
  const [bookingEvent, setBookingEvent] = useState<CalendarEvent | null>(null)

  useEffect(() => {
    let cancelled = false
    fetch(`/api/booking/calendar?uid=${encodeURIComponent(uid)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        if (!cancelled && body?.ok && body.event) setBookingEvent(body.event as CalendarEvent)
      })
      .catch(() => {
        /* keep the fallback event; never drop the button */
      })
    return () => {
      cancelled = true
    }
  }, [uid])

  return <CalendarMenu event={bookingEvent ?? fallbackEvent} uid={uid} />
}

function CalendarMenu({ event, uid }: { event: CalendarEvent | null; uid: string | null }) {
  const [open, setOpen] = useState(false)

  if (!event) return null

  const options: Array<{ label: string; href: string }> = []
  const push = (label: string, href: string | null) => {
    if (href) options.push({ label, href })
  }
  push("Google Calendar", googleCalendarUrl(event))
  // Apple Mail, Fantastical and every desktop client open a .ics directly, so
  // one download covers all of them without naming each.
  push("Apple / iCal (.ics)", uid ? `/api/booking/calendar?uid=${encodeURIComponent(uid)}&format=ics` : null)
  push("Outlook", outlookLiveUrl(event))
  push("Office 365", office365Url(event))
  push("Yahoo", yahooCalendarUrl(event))

  if (!options.length) return null

  return (
    <div className="relative flex flex-col items-center">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={marketingActionClass({ variant: "accent", size: "md" })}
      >
        <CalendarPlus size={16} aria-hidden />
        Add to calendar
      </button>

      {open ? (
        <div className="mt-2 w-[15rem] overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-rule)] bg-white shadow-sm">
          {options.map((option) => (
            <a
              key={option.label}
              href={option.href}
              target={option.href.startsWith("/") ? undefined : "_blank"}
              rel="noopener noreferrer"
              className="block px-4 py-2.5 text-left text-[0.88rem] font-medium text-[var(--color-ink)] transition-colors hover:bg-[var(--color-paper-deep)]"
            >
              {option.label}
            </a>
          ))}
        </div>
      ) : null}
    </div>
  )
}
